import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { InteractorReturnSearch } from '../../interactors/Main'
import InteractorShippingHeaderSearch from '../../interactors/transaction/shipping/InteractorShippingHeaderSearch'
import { InputText } from '../../components/Main'
import Validation, {
  correctionNoteMaxLength,
  correctionNoteMinLength,
  titleMaxLength,
  titleMinLength,
} from '../../constant/global/validation'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import ReturnItemsSection from './ReturnItemsSection'
import { formatDate } from '../../utility/Helpers'

const ReturnView = () => {
  const { t } = useTranslation()
  const refOrder = useRef(null)
  const { account } = useSelector((state) => state.account)
  const pathSegments = window.location.pathname.split('/')
  const uid = pathSegments[pathSegments.length - 1]
  const [loading, setLoading] = useState(true)
  const [loadingShipping, setLoadingShipping] = useState(true)
  const [items, setItems] = useState([])
  const [orderData, setOrderData] = useState({})
  const [orderDataList] = useState([])
  const [dataShipping, setDataShipping] = useState([])
  const { validationInputText, validationInputChar } = Validation()
  const { returnSearch } = InteractorReturnSearch()
  const { shippingHeaderSearch } = InteractorShippingHeaderSearch()
  const [isDataReady, setIsDataReady] = useState(false)
  const [openOrder, setOpenOrder] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCode, setSelectedCode] = useState(null)
  const [detailReturn, setDetailReturn] = useState({
    reference_number_internal: '',
    retur_note: '',
    store_name: '',
    initial_status: '',
    created_date: '',
    retur_approved_date: '',
    retur_lines: [],
  })

  const [totalData, setTotalData] = useState({
    totalOrder: 0,
    shippingCost: 0,
    insuranceCost: 0,
    totalPayment: 0,
  })

  const payloadReturnHeaderSearch = useMemo(
    () => ({
      page: 1,
      limit: 5,
      keywords: {
        uid,
      },
    }),
    [uid],
  )

  const [payloadShipping, setPayloadShipping] = useState({
    page: 1,
    limit: 50,
    keywords: {
      text_search: '',
      uid: '',
      transaction_uid: '',
      account_uid: account?.uid,
      status: '',
      store_uid: '',
    },
  })

  const [error, setError] = useState({
    reference_number_internal: null,
    correction_note: null,
    purchase_order_number: null,
    store_name: null,
    account_uid: null,
    initial_status: null,
    created_date: null,
    correction_approved_date: null,
  })

  const filteredOrders = orderDataList.filter((item) =>
    item?.transaction_code?.toLowerCase()?.includes(searchQuery.toLowerCase()),
  )

  const handleSelectOrder = (val) => {
    setSelectedCode(val.transaction_code)
    handlerInput({
      target: { name: 'transaction_code', value: val.transaction_code },
    })

    setPayloadShipping((prev) => ({
      ...prev,
      keywords: {
        ...prev.keywords,
        transaction_uid: val.uid,
      },
    }))

    setLoadingShipping(true)
    setSearchQuery('') // Reset search setelah pilih
    setOpenOrder(false) // Tutup dropdown
  }

  const handleResponseShipping = useCallback((data) => {
    if (data?.items) {
      const shippingData = data.items.filter((item) => item.initial_status !== 'CANCELLED')
      setDataShipping(shippingData)
      setLoadingShipping(false)
      setIsDataReady(true)
    }
  }, [])

  useEffect(() => {
    if (loadingShipping && payloadShipping.keywords.transaction_uid) {
      shippingHeaderSearch(payloadShipping, handleResponseShipping, () => {
        setLoadingShipping(false)
      })
    }
  }, [loadingShipping, payloadShipping, shippingHeaderSearch, handleResponseShipping])

  const handleReturnHeaderSearch = useCallback((response) => {
    if (response?.items && response.items.length > 0) {
      const item_data = response.items[0]
      setDetailReturn(item_data)

      const dataItems = item_data?.retur_lines || []
      setItems(dataItems)
      setOrderData(item_data)

      setPayloadShipping((prev) => ({
        ...prev,
        keywords: {
          ...prev.keywords,
          transaction_uid: item_data.transaction_uid, // Pastikan UID ini ada di response API
        },
      }))
      setLoadingShipping(true)
    }
  }, [])

  useEffect(() => {
    if (loading) {
      returnSearch(payloadReturnHeaderSearch, handleReturnHeaderSearch, setLoading)
    }
  }, [handleReturnHeaderSearch, returnSearch, payloadReturnHeaderSearch, loading])

  const manipulatedItems = useMemo(() => {
    if (!isDataReady) {
      return []
    }

    return items.map((item) => {
      const matchedShipping = dataShipping.find((val) => val.shipping_code === item.shipping_code)

      const originalShippingItem = matchedShipping?.shipping_items?.find(
        (val) => val.product_uniqueid === item.product_uid,
      )

      return {
        ...item,
        shipping_code: item?.shipping_code || 'No shipping code info',
        product_thumbnail: originalShippingItem?.product_thumbnail,
        product_total_price: item.product_price * item.product_quantity,
        is_deleted: item.is_deleted === 'Y' || item.is_deleted === 1 ? 1 : 0,
      }
    })
  }, [isDataReady, dataShipping, items])

  const handlerInput = (e) => {
    const { name, value } = e.target

    let message = null
    if (name === 'retur_note') {
      message = validationInputChar(
        value,
        correctionNoteMinLength,
        correctionNoteMaxLength,
        t(`form.${name}`),
      )
    } else {
      message = validationInputText(value, titleMinLength, titleMaxLength, t(`form.${name}`))
    }
    setError((prev) => ({ ...prev, [name]: message }))

    setDetailReturn((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const getStatusDateConfig = () => {
    if (detailReturn?.initial_status === 'APPROVED') {
      return {
        label: t('form.return_approved_date'),
        value: detailReturn?.retur_approved_date
          ? formatDate(detailReturn?.retur_approved_date, 'DD MMMM YYYY')
          : '',
        formControl: 'approved_date',
      }
    }
    if (detailReturn?.initial_status === 'REJECTED') {
      return {
        label: t('form.return_rejected_date'),
        value: detailReturn?.retur_rejected_date,
        formControl: 'rejected_date',
      }
    }
    if (detailReturn?.initial_status === 'CANCELLED') {
      return {
        label: t('form.return_cancelled_date'),
        value: detailReturn?.updated_date,
        formControl: '',
      }
    }
    return {
      label: `${t('form.return_approved_date')} / ${t('form.return_rejected_date')}`,
      value: null,
      formControl: '',
    }
  }

  const dateConfig = getStatusDateConfig()

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between mb-3">
        <h6 className="text-primary fw-bold">
          {t('form.return_detail_title')} - {detailReturn?.retur_code}
        </h6>
        <Link
          to={'#'}
          onClick={() => {
            window.location = `/return`
          }}
          className="text-dark btn-outline-secondary btn btn-xs m-2 font-size-12"
        >
          <i className="fas fa-arrow-left px-2"></i> {t('button.back')}
        </Link>
      </div>
      <div className="card p-3 mb-4">
        <div className="row g-3 mb-4">
          <div className="col-md-6 pb-2 pt-1">
            <label className="align-items-md-left font-size-12 font-weight-bold form-label">
              {t('form.return_date')}
            </label>
            <InputText
              value={
                detailReturn?.created_date
                  ? formatDate(detailReturn.created_date, 'DD MMMM YYYY')
                  : ''
              }
              disable
            />
          </div>
          <div className="col-md-6 pb-2 pt-1">
            <label className="align-items-md-left font-size-12 font-weight-bold form-label">
              {t('title.order_number')}
            </label>
            <div className="position-relative" ref={refOrder}>
              <div
                className="form-control d-flex align-items-center"
                style={{ cursor: 'text', height: 'auto' }}
                onClick={() => setOpenOrder(true)}
              >
                <input
                  key="select_order"
                  type="text"
                  className="border-0 w-100"
                  placeholder={detailReturn?.transaction_code || t('title.select_order')}
                  value={searchQuery || selectedCode || ''}
                  onChange={(e) => {
                    if (selectedCode) setSelectedCode(null)

                    setSearchQuery(e.target.value)
                    setOpenOrder(false)
                  }}
                  onFocus={() => setOpenOrder(false)}
                  disabled
                />
                <i
                  className={`fa ${
                    openOrder ? 'fa-chevron-up' : 'fa-chevron-down'
                  } ms-2 text-muted`}
                  style={{ fontSize: 12 }}
                ></i>
              </div>
              {openOrder && (
                <ul
                  className="list-group position-absolute w-100 mt-1 shadow"
                  style={{
                    zIndex: 9999,
                    maxHeight: 200,
                    overflowY: 'auto',
                    borderRadius: 8,
                    backgroundColor: 'white',
                    padding: 0,
                  }}
                >
                  {filteredOrders.length > 0 ? (
                    filteredOrders.map((item) => {
                      const isSelected = selectedCode === item.transaction_code
                      return (
                        <li
                          key={item.transaction_code}
                          className={`list-group-item list-group-item-action ${
                            isSelected ? 'active' : ''
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectOrder(item)}
                        >
                          {item.transaction_code}
                        </li>
                      )
                    })
                  ) : (
                    <li className="list-group-item text-muted small">
                      {t('form.not_found_order')}
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className="col-md-6 pb-2 pt-1">
            <InputText
              label={t('form.reference_number_internal')}
              value={detailReturn?.reference_number_internal || ''}
              maxLength={20}
              onChange={handlerInput}
              error={!!error.reference_number_internal}
              errorMsg={error.reference_number_internal}
              name="reference_number_internal"
              disable
            />
          </div>
          {dateConfig && (
            <div className="col-md-6 pb-2 pt-1">
              <InputText
                label={dateConfig.label}
                value={dateConfig.value ? formatDate(dateConfig.value, 'DD MMMM YYYY') : ''}
                name={dateConfig.formControl}
                disable
              />
            </div>
          )}
          <div className="col-12 pb-2">
            <InputText
              label={t('form.retur_note')}
              value={detailReturn?.retur_note || ''}
              onChange={handlerInput}
              name="retur_note"
              disable
            />
          </div>
        </div>

        {isDataReady ? (
          <ReturnItemsSection
            label={t('form.return_shipping')}
            orderData={orderData}
            dataShipping={dataShipping}
            items={manipulatedItems}
            totalData={totalData}
            setTotalData={setTotalData}
            detailReturn={detailReturn}
            setDetailReturn={setDetailReturn}
          />
        ) : (
          <div className="d-flex justify-content-center p-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReturnView
